import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { openSnackbar } from '~/api/snackbar';
import { PopupTransition } from '~/components/@extended/Transitions';
import { SnackbarProps } from '~/types/snackbar';
import axios from '~/utils/axios';

export function UpdateTempPWModal({ open, onSuccess }: { open: boolean; onSuccess: () => void }) {
  const theme = useTheme();
  const [firstPassword, setFirstPassword] = useState('');
  const [secondPassword, setSecondPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const updatePassword = async () => {
    const regex = new RegExp(
      '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()\\-_=+{};:,<.>])(?=\\S+$).{8,}$',
    );

    if (!regex.test(firstPassword)) {
      setError(
        'Password must contain at least 8 characters, one uppercase, one lowercase, one number, one special character, and no spaces.',
      );
      return;
    } else {
      setError('');
    }

    if (firstPassword !== secondPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/me/profile/update-temp-password', {
        newPassword: firstPassword,
      });

      openSnackbar({
        open: true,
        message: 'Password updated successfully!',
        variant: 'alert',
        duration: 3000,
        alert: {
          color: 'success',
        },
      } as SnackbarProps);

      onSuccess();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      maxWidth="sm"
      TransitionComponent={PopupTransition}
      open={open}
      sx={{ '& .MuiDialog-paper': { p: 0 } }}
    >
      <Box sx={{ display: 'flex' }}>
        <Box>
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            Welcome to the new Topstep!
          </DialogTitle>

          <Box sx={{ px: 2 }}>Please re-enter your password to continue.</Box>

          <DialogContent>
            <InputLabel htmlFor="password-signup">Password</InputLabel>
            <OutlinedInput
              fullWidth
              data-cy="password-input"
              // error={Boolean(touched.password && errors.password)}
              id="password-signup"
              type={showPassword ? 'text' : 'password'}
              value={firstPassword}
              name="password"
              // onBlur={handleBlur}
              onChange={(e) => {
                setFirstPassword(e.target.value);
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                    color="secondary"
                  >
                    {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  </IconButton>
                </InputAdornment>
              }
              placeholder="******"
              inputProps={{}}
            />
            <InputLabel sx={{ mt: '15px' }} htmlFor="password-signup">
              Re-enter Password
            </InputLabel>
            <OutlinedInput
              fullWidth
              data-cy="password-input"
              // error={Boolean(touched.password && errors.password)}
              id="password-signup"
              type={showPassword ? 'text' : 'password'}
              value={secondPassword}
              name="password"
              // onBlur={handleBlur}
              onChange={(e) => {
                setSecondPassword(e.target.value);
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                    color="secondary"
                  >
                    {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                  </IconButton>
                </InputAdornment>
              }
              placeholder="******"
              inputProps={{}}
            />
            <LoadingButton
              sx={{
                mt: '15px',
                '& .MuiLoadingButton-loadingIndicator': {
                  color: '#068e44',
                },
              }}
              disabled={!firstPassword || !secondPassword || firstPassword !== secondPassword}
              variant="contained"
              fullWidth
              loading={loading}
              onClick={updatePassword}
            >
              Update Password
            </LoadingButton>
          </DialogContent>
        </Box>
        <Box
          sx={{
            p: '20px',
            backgroundColor: theme.palette.mode === 'dark' ? '#3b3838' : '#ececec',
            width: '320px',
          }}
        >
          <b>Password Requirements</b>
          <Box>At least 8 characters</Box>
          <Box>At least 1 lower letter (a-z)</Box>
          <Box>At least 1 uppercase letter (A-Z)</Box>
          <Box>At least 1 number (0-9)</Box>
          <Box>At least 1 special characters</Box>
          {error && <Box sx={{ mt: '10px', color: 'red' }}>{error}</Box>}
        </Box>
      </Box>
    </Dialog>
  );
}
